import { inject, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { merge } from 'rxjs';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';
import { Entity } from '../../../../classes/entity.class';
import { User } from '../../../../classes/user.class';
import { CodaltComponent } from '../../../../codalt.component';
import { EntitiesService } from '../../../../services/entities/entities.service';
import { EntityField, EntityType } from '../../../../services/entities/entity-type.class';
import { EntityTypeService, PheFormData, PlanningFormData } from '../../../../services/entity-type.service';
import { UserService } from '../../../../services/user/user.service';

@Component({
    selector: 'app-trackline-planning-has',
    standalone: false,
    templateUrl: './trackline-planning-has.component.html',
    styleUrl: './trackline-planning-has.component.scss',
})
export class TracklinePlanningHasComponent extends CodaltComponent implements OnInit {
    @Input() fieldFormData: PlanningFormData;
    @Input() entityType: EntityType;
    @Input() entityTypes: EntityType[];
    @ViewChild('entitySelectionDialog') entitySelectionDialog: TemplateRef<any>;

    users: User[];
    pheFields: PheFormData[];
    tableHeader: EntityField[];
    allEntityTypes: EntityType[];
    allEntities: Entity[] = [];
    searchQuery = '';
    filteredEntities: Entity[] = [];
    dialogRef: MatDialogRef<any>;

    private entityTypeService = inject(EntityTypeService);
    private entitiesService = inject(EntitiesService);
    private userService = inject(UserService);
    private dialog = inject(MatDialog);

    ngOnInit() {
        this.allEntityTypes = this.entityTypes?.length ? this.entityTypes : [this.entityType];

        this.filterPhesByEntityType();

        this.subscriptions.add(
            this.fieldFormData.form.controls.planningHas.valueChanges.pipe(debounceTime(0)).subscribe(() => {
                this.filterPhesByEntityType();
            })
        );

        this.loadEntities();

        this.subscriptions.add(
            this.userService.getList(true, true, true).subscribe(users => {
                this.users = users;
            })
        );
    }

    filterPhesByEntityType() {
        const allEntityTypeKeys = this.allEntityTypes.map(et => et.key);
        this.tableHeader = this.getMergedTableHeaders(this.allEntityTypes);
        this.pheFields = this.fieldFormData.pheFields.filter(pheField =>
            allEntityTypeKeys.includes(pheField.form.controls.phe.get('entitytype_key').value)
        );
    }

    /**
     * Merges the (already correctly ordered) table headers of every entity type into one list,
     * preserving each entity type's own relative field order instead of re-sorting everything by
     * `displayOrder` again - fields without a `displayOrder` don't compare reliably across entity
     * types, so a single global sort can end up in a different order than `orderTableFields` uses
     * per row. A field shared between entity types acts as an anchor: any field that's unique to an
     * entity type is inserted right after the last anchor seen for that type, matching where it
     * actually appears in that entity type's own (correct) row order.
     */
    private getMergedTableHeaders(entityTypes: EntityType[]): EntityField[] {
        const merged: EntityField[] = [];
        const indexByKey = new Map<string, number>();

        entityTypes.forEach(entityType => {
            let insertAt = 0;

            const headers = this.entityTypeService.getTableHeaders(entityType);

            headers.forEach(field => {
                const existingIndex = indexByKey.get(field.key);

                if (existingIndex !== undefined) {
                    insertAt = existingIndex + 1;
                    return;
                }

                merged.splice(insertAt, 0, field);

                indexByKey.forEach((index, key) => {
                    if (index >= insertAt) {
                        indexByKey.set(key, index + 1);
                    }
                });

                indexByKey.set(field.key, insertAt);

                insertAt++;
            });
        });

        return merged;
    }

    private loadEntities() {
        const entityTypeKeys = this.allEntityTypes.map(et => et.key);
        const begindate = this.fieldFormData.mainPhe?.phe.get('begindate')?.fc;
        const enddate = this.fieldFormData.mainPhe?.phe.get('enddate')?.fc;

        this.subscriptions.add(
            merge(
                ...[begindate, enddate].filter(control => !!control).map(control => control.valueChanges),
                this.fieldFormData.form.controls.planningHas.valueChanges
            )
                .pipe(
                    startWith(null),
                    debounceTime(0),
                    switchMap(() =>
                        this.entitiesService.getByType(
                            entityTypeKeys,
                            { begindate: begindate?.value, enddate: enddate?.value },
                            this.fieldFormData.form.controls.planningHas
                                .getRawValue()
                                .flatMap(ph => [
                                    ph.phe?.entity_id,
                                    ph.phe?.truck_entity_id,
                                    ph.phe?.lowloader_entity_id,
                                ])
                                .filter(id => !!id)
                        )
                    )
                )
                .subscribe(entities => {
                    this.allEntities = entities;

                    this.filteredEntities = this.getAvailableEntities();
                })
        );
    }

    openAddModal() {
        this.searchQuery = '';
        this.filteredEntities = this.getAvailableEntities();
        this.dialogRef = this.dialog.open(this.entitySelectionDialog, {
            width: '600px',
            maxHeight: '80vh',
            autoFocus: this.Utils.isMobile() ? false : 'first-tabbable',
        });
    }

    filterEntities() {
        const availableEntities = this.getAvailableEntities();

        if (!this.searchQuery?.length) {
            this.filteredEntities = availableEntities;
            return;
        }

        const query = this.searchQuery.toLowerCase();

        this.filteredEntities = availableEntities.filter(entity => {
            const driverName = this.users?.find(user => user.id === entity.driver_user_id)?.name;

            return entity.name.toLowerCase().includes(query) || driverName?.toLowerCase().includes(query);
        });
    }

    private getAvailableEntities(): Entity[] {
        const usedEntityIds = this.fieldFormData.form.controls.planningHas.value
            .map(planningHas => planningHas.phe?.entity_id)
            .filter(entityId => !!entityId);

        return this.allEntities.filter(entity => {
            if (entity.outOfService || (usedEntityIds.includes(entity.id) && entity.use_once)) {
                return false;
            }

            return true;
        });
    }

    selectEntity(entity: Entity) {
        const entityType = this.allEntityTypes.find(et => entity.entitytypes.some(entType => entType.key === et.key));

        if (entityType) {
            this.addPheWithEntity(entity, entityType);
        }

        this.dialogRef?.close();
    }

    private addPheWithEntity(entity: Entity, entityType: EntityType) {
        const phe = this.entityTypeService.addPheToForm(this.fieldFormData, entityType);
        this.fieldFormData.form.markAsDirty();
        this.filterPhesByEntityType();
        // timeout so that valueChange on entity Input is triggered
        setTimeout(() => {
            (phe.form.controls.phe.get('entity_id') as any as FormControl).setValue(entity.id);
        });
    }

    addPhe() {
        this.entityTypeService.addPheToForm(this.fieldFormData, this.allEntityTypes[0]);
        this.fieldFormData.form.markAsDirty();
        this.filterPhesByEntityType();
    }

    removePhe(phe: PheFormData) {
        this.entityTypeService.removePheFromForm(this.fieldFormData, phe);
        this.filterPhesByEntityType();
    }

    getEntityTypeIcon(pheField: PheFormData): string {
        const entityTypeKey = pheField.form.controls.phe.get('entitytype_key').value;
        return this.entitiesService.getEntityTypeIcon(entityTypeKey, this.allEntityTypes);
    }
}
